import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { categoriaService, Categoria, CategoriaCreate, CategoriaUpdate } from '../../services/categoriaService';
import { useAuthStore } from '../../store/authStore';

interface CategoriaFormProps {
  categoria?: Categoria | null;
  onClose: () => void;
}

const CategoriaForm = ({ categoria, onClose }: CategoriaFormProps) => {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  const { data: categoriasData } = useQuery(
    ['categorias', user?.tenantId],
    () => categoriaService.getAll(user?.tenantId)
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm<CategoriaCreate | CategoriaUpdate>({
    defaultValues: categoria || {
      name: '',
      description: '',
      parentId: undefined
    }
  });

  const parentId = watch('parentId');

  useEffect(() => {
    if (categoria) {
      reset({
        name: categoria.name,
        description: categoria.description,
        parentId: categoria.parentId,
        isActive: categoria.isActive
      });
    }
  }, [categoria, reset]);

  const createMutation = useMutation(categoriaService.create, {
    onSuccess: () => {
      toast.success('Categoría creada correctamente');
      queryClient.invalidateQueries(['categorias']);
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Error al crear categoría');
    }
  });

  const updateMutation = useMutation(
    (data: CategoriaUpdate) => categoriaService.update(categoria!.id, data),
    {
      onSuccess: () => {
        toast.success('Categoría actualizada correctamente');
        queryClient.invalidateQueries(['categorias']);
        onClose();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Error al actualizar categoría');
      }
    }
  );

  const onSubmit = (data: CategoriaCreate | CategoriaUpdate) => {
    // Si parentId es 0 o undefined, enviarlo como null/undefined
    const submitData = {
      ...data,
      parentId: data.parentId && data.parentId > 0 ? data.parentId : undefined
    };
    
    if (categoria) {
      updateMutation.mutate(submitData as CategoriaUpdate);
    } else {
      createMutation.mutate(submitData as CategoriaCreate);
    }
  };

  // Filtrar categorías para evitar ciclos (no mostrar la categoría actual ni sus hijos como padres)
  const availableParents = categoriasData?.filter(cat => {
    if (categoria) {
      // No mostrar la categoría actual ni sus descendientes
      return cat.id !== categoria.id && cat.parentId !== categoria.id;
    }
    return true;
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-bold mb-4">
          {categoria ? 'Editar Categoría' : 'Nueva Categoría'}
        </h2>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre *
            </label>
            <input
              {...register('name', { required: 'Nombre es requerido' })}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descripción
            </label>
            <textarea
              {...register('description')}
              rows={3}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Categoría Padre
            </label>
            <select
              {...register('parentId', { valueAsNumber: true })}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value={0}>Sin categoría padre (Raíz)</option>
              {availableParents?.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Deja en "Sin categoría padre" para crear una categoría raíz
            </p>
          </div>

          {categoria && (
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  {...register('isActive')}
                  className="rounded"
                />
                <span className="text-sm font-medium text-gray-700">Activa</span>
              </label>
            </div>
          )}

          <div className="flex justify-end space-x-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={createMutation.isLoading || updateMutation.isLoading}
              className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
            >
              {createMutation.isLoading || updateMutation.isLoading ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CategoriaForm;

